/**
 * <ReportsDomainIndex /> — generic landing page for any
 * `/reports/{domain}` route per M3X.REPORTS §0.8.
 *
 * Renders the searchable / filterable grid of all reports in a
 * domain. Each card has a 1-line description, category chip, and
 * a `Run report →` button that navigates to
 * `/reports/{domain}/{report.id}`.
 *
 * Used by `/reports/leaves`, `/reports/payroll`, `/reports/loans`,
 * `/reports/excuses`, etc. — single component, domain-scoped via
 * the `domain` prop.
 *
 * The `<ReportsLauncherDrawer />` (the Operations-side launcher)
 * shares the same internal card renderer for visual consistency.
 */

import { ArrowRight, FileText, Search, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  type ReportCategory,
  type ReportDomain,
  categoriesForDomain,
  reportUrl,
  reportsForDomain,
} from "../../lib/reports/catalog";
import { ComingSoonBadge } from "./ComingSoonBadge";
import { EmptyState } from "./EmptyState";
import { PageHeader } from "./PageHeader";

export interface ReportsDomainIndexProps {
  domain: ReportDomain;
  /** Localized title shown in the page header. */
  title: string;
  /** Localized description shown under the title. */
  description?: string;
  /** Breadcrumbs ending at the domain — Reports > {domain}. */
  breadcrumbsLabel?: string;
}

const CATEGORY_LABEL_KEYS: Record<ReportCategory, string> = {
  operational: "reports.cat_operational",
  compliance: "reports.cat_compliance",
  cost: "reports.cat_cost",
  trend: "reports.cat_trend",
  analytics: "reports.cat_analytics",
};

const CATEGORY_DOT_CLASS: Record<ReportCategory, string> = {
  operational: "bg-brand-teal",
  compliance: "bg-rose-500",
  cost: "bg-brand-blue",
  trend: "bg-brand-purple",
  analytics: "bg-brand-deep",
};

export function ReportsDomainIndex({
  domain,
  title,
  description,
  breadcrumbsLabel,
}: ReportsDomainIndexProps) {
  const { t } = useTranslation();
  const all = useMemo(() => reportsForDomain(domain), [domain]);
  const cats = useMemo(() => categoriesForDomain(domain), [domain]);

  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState<ReportCategory | "all">("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((r) => {
      if (activeCat !== "all" && r.category !== activeCat) return false;
      if (!q) return true;
      const title = t(r.titleKey, r.titleKey).toLowerCase();
      const desc = t(r.descriptionKey, r.descriptionKey).toLowerCase();
      return title.includes(q) || desc.includes(q);
    });
  }, [all, query, activeCat, t]);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={title}
        subtitle={description}
        breadcrumbs={[
          {
            label: t("reports.breadcrumb_root", "Reports"),
            href: "/reports",
          },
          { label: breadcrumbsLabel ?? title },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search
            size={14}
            aria-hidden
            className="absolute start-3 top-1/2 -translate-y-1/2 text-text-tertiary"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("reports.search_placeholder", "Search reports…")}
            className="w-full rounded-sm border border-border bg-surface-default ps-8 pe-3 py-1.5 text-sm placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <CategoryChip
            label={t("reports.cat_all", "All")}
            active={activeCat === "all"}
            onClick={() => setActiveCat("all")}
          />
          {cats.map((cat) => (
            <CategoryChip
              key={cat}
              label={t(CATEGORY_LABEL_KEYS[cat], cat)}
              dotClass={CATEGORY_DOT_CLASS[cat]}
              active={activeCat === cat}
              onClick={() => setActiveCat(cat)}
            />
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={FileText}
          headline={t("reports.empty_title", "No reports match")}
          description={t(
            "reports.empty_desc",
            "Adjust the search or category filter to see more reports.",
          )}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((r) => (
            <div
              key={r.id}
              className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface-default p-4"
            >
              <div className="flex items-center gap-2">
                <span
                  aria-hidden
                  className={`h-2 w-2 rounded-full ${CATEGORY_DOT_CLASS[r.category]}`}
                />
                <span className="text-[10px] uppercase tracking-wide text-text-tertiary">
                  {t(CATEGORY_LABEL_KEYS[r.category], r.category)}
                </span>
                {r.aiTier !== "template" && (
                  <span
                    className="inline-flex items-center gap-0.5 rounded-full bg-ai-bg px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-ai-deep"
                    title={t(
                      "reports.ai_tier_hint",
                      "AI-narrated insight at top of this report",
                    )}
                  >
                    <Sparkles size={9} aria-hidden /> AI
                  </span>
                )}
                {!r.shipped && (
                  <ComingSoonBadge
                    expectedRelease={t(
                      "reports.next_pack_label",
                      "Next pack",
                    )}
                  >
                    {t("reports.coming_label", "Coming soon")}
                  </ComingSoonBadge>
                )}
              </div>
              <h3 className="text-sm font-semibold text-text-primary">
                {t(r.titleKey, r.titleKey)}
              </h3>
              <p className="text-xs text-text-tertiary leading-relaxed">
                {t(r.descriptionKey, r.descriptionKey)}
              </p>
              <div className="mt-1 flex items-center justify-end">
                {r.shipped ? (
                  <Link
                    to={reportUrl(r.domain, r.id)}
                    className="inline-flex items-center gap-1 rounded-sm bg-brand-blue px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-deep"
                  >
                    {t("reports.run_report", "Run report")}
                    <ArrowRight size={12} aria-hidden className="rtl:rotate-180" />
                  </Link>
                ) : (
                  <span className="text-[10px] text-text-tertiary italic">
                    {t("reports.coming_label", "Coming soon")}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryChip({
  label,
  dotClass,
  active,
  onClick,
}: {
  label: string;
  dotClass?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs",
        active
          ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
          : "border-border-subtle text-text-secondary hover:bg-surface-sunken",
      ].join(" ")}
    >
      {dotClass && (
        <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
      )}
      {label}
    </button>
  );
}

export default ReportsDomainIndex;
